// REST API hooks for workflow forms
// Based on web-frontend/src/api/useForms.ts
// Uses the Rails core API at api.withsplice.com/v2

import { useState, useCallback } from "react";

const WORKFLOW_FORMS_API_BASE =
  import.meta.env.VITE_WORKFLOW_FORMS_API_BASE_URL ||
  "https://api-staging.withsplice.com/v2";

// API Routes for workflow forms
const WORKFLOW_FORMS_ROUTES = {
  WORKFLOW_FORMS: "/workflow-forms",
  WORKFLOW_FORMS_BOOKING: "/workflow-forms/business/before-booking",
  WORKFLOW_FORM_AFTER_BOOKING: "/workflow-forms/business/after-booking",
};

// Types for workflow forms
export interface WorkflowFormField {
  id: string;
  fieldType: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  options?: string[];
  description?: string;
  order?: number;
}

export interface WorkflowForm {
  id: string;
  submissionId?: string;
  name: string;
  description?: string;
  fields: WorkflowFormField[];
  status?: string;
  sendCondition?: string;
  services?: string[];
}

export interface WorkflowFormAnswers {
  [fieldId: string]: string | string[] | boolean;
}

export interface BeforeBookingRequest {
  businessId: string;
  services: string[];
  phone: string;
  callingCode: string;
}

export interface AfterBookingRequest {
  businessId: string;
  services: string[];
  clientId: string;
  sendCondition: "AFTER_BOOKING_CONFIRMED" | "AFTER_APPOINTMENT";
  appointmentId: string;
}

export interface SubmitAnswersRequest {
  submissionId: string;
  answers: WorkflowFormAnswers;
  phone: string;
  clientId: string;
  appointmentId?: string;
}

interface ApiResponse<T> {
  status: number;
  data?: T;
  message?: string;
  errors?: Array<{ message: string }>;
}

/**
 * Hook to trigger workflow form before booking
 * Called when user enters phone number during checkout
 */
export const useWorkflowBeforeBooking = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<WorkflowForm | null>(null);

  const triggerBeforeBooking = useCallback(
    async ({ businessId, services, phone, callingCode }: BeforeBookingRequest) => {
      setLoading(true);
      setError(null);

      try {
        const url = `${WORKFLOW_FORMS_API_BASE}${WORKFLOW_FORMS_ROUTES.WORKFLOW_FORMS_BOOKING}/${businessId}`;
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            services,
            phone: `${callingCode}${phone}`,
          }),
        });

        if (!response.ok) {
          // Not all requests will have a form - 404 is expected when no form exists
          if (response.status === 404) {
            setData(null);
            return null;
          }
          throw new Error(`Failed to fetch workflow form: ${response.status}`);
        }

        const result = await response.json();
        
        // Handle the response - it may be wrapped in a data property
        const formData = result.data || result;
        setData(formData);
        return formData;
      } catch (err) {
        console.error("[WorkflowForm] Error triggering before booking:", err);
        setError(err as Error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    triggerBeforeBooking,
    data,
    loading,
    error,
    reset: () => {
      setData(null);
      setError(null);
    },
  };
};

/**
 * Hook to trigger workflow form after booking is confirmed
 * Called after appointment is successfully created
 */
export const useTriggerFormAfterAppointment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const triggerAfterAppointment = useCallback(
    async ({
      businessId,
      services,
      clientId,
      sendCondition,
      appointmentId,
    }: AfterBookingRequest) => {
      setLoading(true);
      setError(null);

      try {
        const url = `${WORKFLOW_FORMS_API_BASE}${WORKFLOW_FORMS_ROUTES.WORKFLOW_FORM_AFTER_BOOKING}`;
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            client_id: clientId,
            business_id: businessId,
            services,
            send_condition: sendCondition,
            appointment_id: appointmentId,
          }),
        });

        if (!response.ok) {
          // 404 is expected when no form exists for this condition
          if (response.status === 404) {
            return null;
          }
          throw new Error(
            `Failed to trigger after-appointment form: ${response.status}`
          );
        }

        const result = await response.json();
        return result.data || result;
      } catch (err) {
        console.error(
          "[WorkflowForm] Error triggering after appointment:",
          err
        );
        setError(err as Error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    triggerAfterAppointment,
    loading,
    error,
  };
};

/**
 * Hook to submit workflow form answers
 */
export const useSubmitWorkflowAnswers = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const submitAnswers = useCallback(
    async ({
      submissionId,
      answers,
      phone,
      clientId,
      appointmentId,
    }: SubmitAnswersRequest) => {
      setLoading(true);
      setError(null);

      try {
        const url = `${WORKFLOW_FORMS_API_BASE}${WORKFLOW_FORMS_ROUTES.WORKFLOW_FORMS}/${submissionId}/answers`;
        const response = await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            answers,
            phone,
            client_id: clientId,
            ...(appointmentId && { appointment_id: appointmentId }),
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to submit form answers: ${response.status}`);
        }

        const result = await response.json();
        return result.data || result;
      } catch (err) {
        console.error("[WorkflowForm] Error submitting answers:", err);
        setError(err as Error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    submitAnswers,
    loading,
    error,
  };
};
